import React, { useEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';

const LOGO_URL = "/images/logo.webp";

const LoadingScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const logoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Animate progress
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 50);

    // Logo animation timeline
    const tl = gsap.timeline();

    // Initial entrance - logo drops in with bounce
    tl.fromTo('.logo-container',
      { scale: 0, rotation: -180, opacity: 0, y: -100 },
      { scale: 1, rotation: 0, opacity: 1, y: 0, duration: 1.2, ease: 'elastic.out(1, 0.6)' }
    )
      // Pizza toss animation - up and spin
      .to('.logo-container', {
        y: -40,
        rotation: 15,
        duration: 0.4,
        ease: 'power2.out'
      })
      .to('.logo-container', {
        y: 0,
        rotation: -10,
        duration: 0.3,
        ease: 'power2.in'
      })
      .to('.logo-container', {
        y: -30,
        rotation: 10,
        duration: 0.3,
        ease: 'power2.out'
      })
      .to('.logo-container', {
        y: 0,
        rotation: 0,
        duration: 0.3,
        ease: 'bounce.out'
      })
      // Pulse effect
      .to('.logo-container', {
        scale: 1.1,
        duration: 0.3,
        ease: 'power2.inOut',
        yoyo: true,
        repeat: 1
      });

    // Floating particles animation
    gsap.to('.particle', {
      y: -20,
      opacity: 0.8,
      duration: 1.5,
      stagger: 0.2,
      repeat: -1,
      yoyo: true,
      ease: 'power1.inOut'
    });

    // Tagline animation
    gsap.fromTo('.loading-tagline',
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 1, delay: 1.8, ease: 'power3.out' }
    );

    // Glow pulse effect on logo
    gsap.to('.logo-glow', {
      opacity: 0.6,
      scale: 1.2,
      duration: 1,
      repeat: -1,
      yoyo: true,
      ease: 'power1.inOut'
    });

    // Auto transition
    const timeout = setTimeout(() => {
      gsap.to('.loading-screen', {
        opacity: 0,
        duration: 0.5,
        onComplete: onComplete
      });
    }, 3500);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [onComplete]);

  return (
    <div ref={containerRef} className="loading-screen fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-red-600 via-red-500 to-yellow-400 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating particles */}
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="particle absolute w-4 h-4 rounded-full bg-white/20"
            style={{
              left: `${10 + i * 12}%`,
              top: `${20 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.2}s`
            }}
          />
        ))}

        {/* Large decorative circles */}
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-yellow-400/20 blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-red-800/20 blur-3xl"></div>
      </div>

      {/* Logo Animation Container */}
      <div className="relative mb-6">
        {/* Glow effect behind logo */}
        <div className="logo-glow absolute inset-0 rounded-full bg-yellow-300/40 blur-2xl scale-150"></div>

        {/* Logo */}
        <div ref={logoRef} className="logo-container relative">
          <img
            src={LOGO_URL}
            alt="PizzaToons Logo"
            className="w-48 h-48 md:w-64 md:h-64 object-contain drop-shadow-2xl"
            style={{ filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))' }}
          />
        </div>

        {/* Sparkle effects around logo */}
        <div className="absolute -top-4 -right-4 w-3 h-3 bg-yellow-300 rounded-full animate-ping"></div>
        <div className="absolute -bottom-2 -left-4 w-2 h-2 bg-white rounded-full animate-ping" style={{ animationDelay: '0.3s' }}></div>
        <div className="absolute top-1/2 -right-6 w-2 h-2 bg-yellow-200 rounded-full animate-ping" style={{ animationDelay: '0.6s' }}></div>
      </div>

      {/* Tagline */}
      <p className="loading-tagline text-white text-center text-lg md:text-2xl max-w-md px-4 font-medium drop-shadow-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>
        "From a Small Dream to a Growing Pizza Family"
      </p>

      {/* Progress Bar */}
      <div className="mt-8 w-56 h-2 bg-white/30 rounded-full overflow-hidden backdrop-blur-sm">
        <div
          className="h-full bg-gradient-to-r from-yellow-300 to-yellow-400 rounded-full transition-all duration-100 ease-out shadow-lg"
          style={{ width: `${progress}%` }}
        ></div>
      </div>

      {/* Loading text */}
      <p className="mt-3 text-white/70 text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
        Loading deliciousness...
      </p>
    </div>
  );
};

export default LoadingScreen;
